/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

/* eslint-disable max-len */

import { IHeaderViewProps } from '@msdyn365-commerce-modules/header/dist/types';
import { CartIconComponent } from '@msdyn365-commerce/components';
import {
    Button,
    getPayloadObject,
    getTelemetryAttributes,
    getTelemetryObject,
    Module,
    Node,
    Popover,
    UncontrolledTooltip
} from '@msdyn365-commerce-modules/utilities';
import * as Msdyn365 from '@msdyn365-commerce/core';
import React, { useEffect, useState } from 'react';
import { TierType } from '../../../modules/dobbies-add-product-to-cart/components/add-product-to-cart';
import {
    IHeaderProps,
    IMyAccountLinksBeforeLoginData,
    IMyAccountLinksData,
    IStoreLinkUrlData
} from '../definition-extensions/header.ext.props.autogenerated';
import { IHeaderData } from './header.data';
import { _getLoyaltyInfo } from './account-loyalty.view';
import { Modal, ModalBody, ModalHeader, ModalFooter } from '@msdyn365-commerce-modules/utilities';
import { updateCustomerAttributes, updateCustomerAttributesInput } from '@msdyn365-commerce-modules/retail-actions';
import { AttributeDefinition, CustomerAttribute } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import axios, { AxiosRequestConfig } from 'axios';
import { CartItemsChangesInput, getCartItemsChangesAction } from '../actions/get-cart-items-updates';
import { IChanges, IVSICompareCartDataResponse } from '../utilities/persist-cart-utils';

const headerView: React.FC<IHeaderViewProps & IHeaderProps<IHeaderData>> = props => {
    const {
        HeaderTag,
        HeaderContainer,
        HeaderTopBarContainer,
        MobileMenuContainer,
        MobileMenuBodyContainer,
        MobileMenuLinksContainer,
        signInLink,
        signOutLink,
        wishListIconMobile,
        data: {
            accountInformation: { result }
        },
        context: {
            request: query,
            actionContext: {
                requestContext: { cookies }
            }
        },
        resources: { cartLabel, cartQtyLabel }
    } = props;
    const searchTermString: string | object | undefined =
        typeof query === 'object' && query && query.hasOwnProperty('query') && query.query && query.query.hasOwnProperty('q')
            ? query.query.q
            : 'all';

    const _getSlot = (slotName: string): React.ReactNode[] | null => {
        const { slots } = props;
        if (slotName in slots) {
            const slot = slots[slotName as keyof typeof slots];
            return slot?.length ? slot : null;
        }
        return null;
    };

    const pageType = props.context.request.telemetryPageName;
    const pageUrl = props.context.request.url.requestUrl.href.toString();
    /* VSI Customization - START - 22/10/20 */
    const isLoggedIn = result && result.Name ? true : false;
    /* State manages popup menu visibility */
    const [popOver, togglePopOver] = useState(false);

    const setCookie = (name: string, value: string, deltaDaysFromToday: number) => {
        const { cookies } = props.context.request;
        const expirationDate = new Date(Date.now());
        expirationDate.setTime(expirationDate.getTime() + deltaDaysFromToday * 24 * 60 * 60 * 1000);

        cookies.set(name, value, {
            expires: deltaDaysFromToday ? expirationDate : null,
            isEssential: true
        } as {});
    };

    // const cookieExist = (name: string) => {
    //     const { cookies } = props.context.request;
    //     // if cookie exists, don't display
    //     if ((cookies.get as (name: string, isEssential: boolean) => Msdyn365.ICookieValue<string>)(name, true).status === 'FOUND') {
    //         return true;
    //     }

    //     return false;
    // }

    /* VSI Customization - START - 29/08/23 */
    const [isSignupErrorModalOpen, setSignupModalOpen] = useState(false);
    const [shouldCloseSignupErrorModal, setShouldCloseSignupErrorModal] = useState(false);
    const onSignupErrorModalClose = () => {
        setSignupModalOpen(false);
        setCookie(
            'failed_sign_in_user',
            props.context.actionContext.requestContext.user.emailAddress
                ? props.context.actionContext.requestContext.user.emailAddress
                : '',
            1
        );
    };
    const redirectToSignin = () => {
        const signInUrl = `${props.context.actionContext.requestContext.url.requestUrl.origin}/_msdyn365/signin`;
        window.location.href = signInUrl;
    };

    if (Msdyn365.msdyn365Commerce.isBrowser) {
        if (!props.context.actionContext.requestContext.user.retailServerErrorCode || window.location.href.includes('_msdyn365/signin')) {
            setCookie('failed_sign_in_user', '', -2);
        }
        const cookies = props.context.actionContext.requestContext.cookies;
        let cookieForUserExists = false;

        const cookieValue = cookies.get('failed_sign_in_user');
        cookieForUserExists = cookieValue && cookieValue.value === props.context.actionContext.requestContext.user.emailAddress;
        if (props.context.actionContext.requestContext.user.retailServerErrorCode && !cookieForUserExists) {
            if (!isSignupErrorModalOpen && !shouldCloseSignupErrorModal) {
                setSignupModalOpen(true);
                setShouldCloseSignupErrorModal(true);
            }
        }
    }

    /*  condition either to show or hide the header  */
    if (props.context.request.query!.onlyBody && props.context.request.query!.onlyBody === 'true') {
        return null;
    }

    useEffect(() => {
        cookies.remove('tier');
        if (isLoggedIn) {
            const tierName = getTierId(props);
            if (tierName === 'Club Plus') {
                cookies.set('tier', TierType.ClubPlus);
            } else if (tierName === 'Club Premium') {
                cookies.set('tier', TierType.ClubPremium);
            } else if (tierName === 'Club') {
                cookies.set('tier', TierType.Club);
            } else if (tierName === 'Club Plus Free Trial') {
                cookies.set('tier', TierType.ClubPlusFreeTrial);
            }
        } else {
            togglePopOver(false);
        }
        updateAttribute();

        if (props.context.request.user.isAuthenticated) {
            let hasContactableEmail = false;
            let isNewUser = false;
            // @ts-ignore
            const claims = props.context.actionContext.requestContext['User']['Claims'];
            if (Array.isArray(claims)) {
                for (const claim of claims) {
                    if (claim['Key'] === 'newUser' && claim['Value'] === 'true') {
                        isNewUser = true;
                    }
                    if (claim['Key'] === 'mktOptions' && claim['Value'].includes('Contactable Email')) {
                        hasContactableEmail = true;
                    }
                }
            }

            if (!isNewUser && !hasContactableEmail) {
                const contactableEmailAttr = props.data.customerInformation.result?.Attributes?.find(
                    attr => attr.Name === 'Contactable Email'
                );
                if (contactableEmailAttr && contactableEmailAttr.AttributeValue?.BooleanValue) {
                    hasContactableEmail = true;
                }
            }

            if (!isDotDigitalAccount()) {
                callDotDigitalCreate(hasContactableEmail);
            } else if (!hasContactableEmail) {
                unsubscribeFromDotDigital();
            }
        }
        const cartItemChanges = props.data.cartItemsChanges.result;
        if (cartItemChanges) {
            showChangedCartItemsNotification(cartItemChanges);
        }
        startCheckingForCartItemsChanges();
    }, []);

    const startCheckingForCartItemsChanges = () => {
        if (props.context.request.app.config.fetchCartItemsChangesInterval) {
            setInterval(() => {
                getCartItemsChangesAction(new CartItemsChangesInput(), props.context.actionContext)
                    .then(res => {
                        showChangedCartItemsNotification(res);
                    })
                    .catch(e => {
                        props.context.actionContext.telemetry.error(e);
                    });
            }, props.context.request.app.config.fetchCartItemsChangesInterval * 1000);
        }
    };

    // const constructCartChangesMessage = (allChanges: IChanges[]): JSX.Element | null => {
    //     // TODO: Add UI as per the designs
    //     return <></>;
    // };

    const showChangedCartItemsNotification = (cartItemChanges: IVSICompareCartDataResponse) => {
        const allChanges: IChanges[] = [];
        if (cartItemChanges?.Changes?.length) {
            const changes: IChanges[] = cartItemChanges.Changes;
            for (let i = 0; i < changes.length; i++) {
                if (changes[i].OriginalValue !== changes[i].UpdatedValue) {
                    allChanges.push(changes[i]);
                }
            }
        }
        // if (allChanges.length) {
        //     const constructedMessage = constructCartChangesMessage(allChanges);
        //     // TODO: Show Toast/Notification of the message
        // }
    };

    const callDotDigitalCreate = (hasContactableEmail: boolean) => {
        var data = JSON.stringify({
            emailDataRequest: {
                Email: props.data.customerInformation.result?.Email,
                OptInType: 'Single',
                EmailType: 'HTML',
                DataFields: [
                    {
                        Key: 'FIRSTNAME',
                        Value: props.data.customerInformation.result?.FirstName
                    },

                    {
                        Key: 'LASTNAME',
                        Value: props.data.customerInformation.result?.LastName
                    }
                ]
            }
        });

        var config: AxiosRequestConfig = {
            method: 'post',
            url: 'https://scu40oznal298244666-rs.su.retail.dynamics.com/Commerce/VSICreateDotDigitalContact?api-version=7.3',
            headers: {
                accept: 'application/json',
                'accept-language': 'en-GB',
                authorization: `id_token ${props.context.actionContext.requestContext.user.token}`,
                'content-type': 'application/json',
                contenttype: 'application/json',
                oun: props.context.request.apiSettings.oun
            },
            data: data
        };

        axios(config)
            .then(async res => {
                console.log(res);
                await updateCustomerDotDigitalAttribute();
                if (!hasContactableEmail) {
                    unsubscribeFromDotDigital();
                }
                // createOrUpdateDotDigitalContact();
            })
            .catch(err => {
                console.log(err);
            });
    };

    const isDotDigitalAccount = () => {
        const { customerInformation } = props.data;
        const attributes = customerInformation.result?.Attributes;
        const dotDigitalAttr = attributes?.find(attr => attr.Name === 'isDotDigitalAccount');
        if (dotDigitalAttr && dotDigitalAttr.AttributeValue?.StringValue === 'True') {
            return true;
        }
        return false;
    };

    const updateCustomerDotDigitalAttribute = async () => {
        const { customerInformation } = props.data;
        let attributes = customerInformation.result?.Attributes;
        const existingDDAttr = attributes?.find(attr => attr.Name === 'isDotDigitalAccount');
        if (!existingDDAttr || existingDDAttr.AttributeValue?.StringValue !== 'True') {
            attributes = customerInformation.result?.Attributes?.filter(attr => attr.Name !== 'isDotDigitalAccount');
            const defaultAttribute = props.data.attributeDefinitions.result?.find(
                attr => attr.Name?.toLowerCase() === 'isDotDigitalAccount'.toLowerCase()
            );
            let isDotDigitalAttr: CustomerAttribute = {
                ['AttributeValue']: {
                    // @ts-ignore
                    '@odata.type': '#Microsoft.Dynamics.Commerce.Runtime.DataModel.CommercePropertyValue',
                    StringValue: 'True'
                },
                ['@odata.type']: '#Microsoft.Dynamics.Commerce.Runtime.DataModel.CustomerAttribute',
                ['ExtensionProperties@odata.type']: '#Collection(Microsoft.Dynamics.Commerce.Runtime.DataModel.CommerceProperty)',
                ['NameTranslations@odata.type']: '#Collection(Microsoft.Dynamics.Commerce.Runtime.DataModel.TextValueTranslation)',
                ['NameTranslations']: [],
                ['DataTypeValue']: defaultAttribute && defaultAttribute.DataTypeValue,
                ['KeyName']: defaultAttribute && defaultAttribute.Name,
                ['Name']: defaultAttribute && defaultAttribute.Name,
                ['RecordId']: defaultAttribute && defaultAttribute.RecordId,
                ['ExtensionProperties']: []
            };
            attributes?.push(isDotDigitalAttr);
            const input = new updateCustomerAttributesInput(
                customerInformation.result?.AccountNumber || '',
                attributes || [],
                props.context.request.apiSettings
            );
            await updateCustomerAttributes(input, props.context.actionContext).catch();
        }
    };

    const unsubscribeFromDotDigital = () => {
        var data = JSON.stringify({
            emailDataRequest: {
                Identifiers: {
                    Email: props.data.customerInformation.result?.Email
                },
                ChannelProperties: {
                    Email: {
                        ResubscribeOptions: {
                            ResubscribeWithNoChallenge: true
                        },
                        EmailType: 'html',
                        OptInType: 'single',
                        Status: 'unsubscribed'
                    }
                }
            }
        });

        var config: AxiosRequestConfig = {
            method: 'post',
            url: 'https://scu40oznal298244666-rs.su.retail.dynamics.com/Commerce/VSICreateOrUpdateDotDigitalContact?api-version=7.3',
            headers: {
                accept: 'application/json',
                'accept-language': 'en-GB',
                authorization: `id_token ${props.context.actionContext.requestContext.user.token}`,
                'content-type': 'application/json',
                contenttype: 'application/json',
                oun: props.context.request.apiSettings.oun
            },
            data: data
        };

        axios(config)
            .then(function(response) {
                console.log(JSON.stringify(response.data));
            })
            .catch(function(error) {
                console.log(error);
            });
    };

    const updateAttribute = async () => {
        const { attributeDefinitions, customerInformation } = props.data;
        const attributeDefinition: AttributeDefinition[] = (attributeDefinitions && attributeDefinitions.result) || [];
        let filteredMktOptions = attributeDefinition
            .map(setAttrDefaultValues)
            .filter(attr => attr.Name === 'Contactable Post' || attr.Name === 'Contactable Mobile' || attr.Name === 'Contactable Email');
        const customer = customerInformation.result!;
        // @ts-ignore
        const mktoption = props.context.request['User']['Claims'];
        let filtered = mktoption?.filter((itm: { Key: string }) => itm.Key === 'mktOptions');
        if (filtered && filtered.length > 0) {
            let splited = filtered[0].Value.split(',');
            let updatedMktOptions = filteredMktOptions?.map(attr => {
                if (splited.includes(attr.Name)) {
                    return { ...attr, AttributeValue: { ...attr.AttributeValue, BooleanValue: true } };
                }
                return attr;
            });
            if (customer.Attributes?.length === 0 && filtered.length > 0) {
                const input = new updateCustomerAttributesInput(
                    customer.AccountNumber,
                    updatedMktOptions || [],
                    props.context.request.apiSettings
                );
                await updateCustomerAttributes(input, props.context.actionContext).catch();
            }
        }
    };
    // Function for setting attribute default value
    const setAttrDefaultValues = (attribute: CustomerAttribute) => {
        return {
            Attribute: 0,
            DataTypeValue: attribute.DataTypeValue,
            KeyName: attribute.Name,
            Name: attribute.Name,
            RecordId: attribute.RecordId,
            Units: '',
            AttributeValue: {
                BooleanValue: false
            },
            ExtensionProperties: [],
            NameTranslations: []
        };
    };

    /* popOverRef is used to create popup menu */
    const popOverRef: React.RefObject<HTMLButtonElement> = React.createRef();
    /* VSI Customization - START - 23/10/20 */
    /* Added store link and text from configs with tooltips */
    const {
        config: { showStoreTooltip, storeLinkUrl }
    } = props;
    const storeIconRef: React.RefObject<HTMLAnchorElement> = React.createRef();
    const storeLinkData: Msdyn365.ILinksData = getStoreLinkUrl(storeLinkUrl!);
    /* VSI Customization - START - 29/08/23 */
    const multipleAccountsMsg = props.config.signupErrorModalDescriptionMultipleAccounts
        ? props.config.signupErrorModalDescriptionMultipleAccounts
        : props.context.actionContext.requestContext.user.retailServerErrorCode;
    const incompleteAccountsMsg = props.config.signupErrorModalDescriptionIncompleteRecord
        ? props.config.signupErrorModalDescriptionIncompleteRecord
        : props.context.actionContext.requestContext.user.retailServerErrorCode;
    const defaultSignupErrorMsg = props.config.signupErrorModalDefaultMessage
        ? props.config.signupErrorModalDefaultMessage
        : props.context.actionContext.requestContext.user.retailServerErrorCode;
    const signupErrorMsg =
        props.context.actionContext.requestContext.user.retailServerErrorCode &&
        props.context.actionContext.requestContext.user.retailServerErrorCode.includes('MultipleCustomerAccountsFound')
            ? multipleAccountsMsg
            : props.context.actionContext.requestContext.user.retailServerErrorCode &&
              props.context.actionContext.requestContext.user.retailServerErrorCode.includes('CustomerAccountMissing')
            ? incompleteAccountsMsg
            : defaultSignupErrorMsg;
    const cartIconSlot = _getSlot('cartIcon');
    const telemetryContent = getTelemetryObject(props.context.request.telemetryPageName!, props.friendlyName, props.telemetry);
    return (
        <Module {...HeaderTag}>
            <Node {...HeaderContainer}>
                <Node tag='div' className='container'>
                    <Node {...HeaderTopBarContainer}>
                        <Node className='ms-header__brand-logo'>
                            {props.navIcon}
                            {props.logo}
                        </Node>
                        <Node className='ms-header__search-container'>{_renderReactFragment(props.search)}</Node>
                        <Node className='ms-header__header-sidebar'>
                            <Node className='ms-header__header-sidebar__group-icons'>
                                <Msdyn365.Link link={storeLinkData} editProps={{ requestContext: props.context.request }} />
                                {/* Used existing component for tooltips */}
                                {showStoreTooltip && storeLinkUrl && (
                                    <UncontrolledTooltip trigger={'hover focus'} target={storeIconRef}>
                                        {storeLinkUrl.linkText}
                                    </UncontrolledTooltip>
                                )}
                                {_renderDesktopAccountBlock(props, popOver, togglePopOver, popOverRef)}
                                {props.wishListIconDesktop}
                                {/* {props.cartIcon} */}
                                {cartIconSlot ? (
                                    cartIconSlot
                                ) : (
                                    <CartIconComponent
                                        cartLabel={cartLabel}
                                        cartQtyLabel={cartQtyLabel}
                                        context={props.context}
                                        id={props.id}
                                        typeName={props.typeName}
                                        telemetryContent={telemetryContent}
                                        data={{ cart: props.data.cart.result }}
                                    />
                                )}
                            </Node>
                        </Node>
                    </Node>
                </Node>
                {/* VSI-Customization -- START 29/10/20 Render Promo Banner Slot */}
                <Node tag='div' className='ms-promo-banner__slot'>
                    {props.slots.promoBanner}
                </Node>
                {/* VSI-Customization -- END  */}
                {/* VSI-Customization -- START 13/06/22 Render Dobbies CTA Section Slot */}
                <Node tag='div' className='ms-promo-banner__slot'>
                    {props.slots.dobbiesCTASection}
                </Node>
                {/* VSI-Customization -- Render Dobbies store notification */}
                {props.slots.storeBasedNotification && (
                    <Node tag='div' className='ms-promo-banner__slot'>
                        {props.slots.storeBasedNotification}
                    </Node>
                )}
                {/* VSI-Customization -- END  */}
                <Node {...MobileMenuContainer}>
                    <Node {...MobileMenuBodyContainer}>
                        {props.MobileMenuHeader}
                        {_renderReactFragment(props.menuBar)}
                        <Node {...MobileMenuLinksContainer}>
                            {signInLink}
                            {/* True value indicates it's for Mobile view */}
                            {!isLoggedIn && _getSignUpButton(props, true)}
                            {signOutLink}
                            {isLoggedIn && wishListIconMobile}
                        </Node>
                    </Node>
                </Node>
                {_renderReactFragment(props.menuBar)}
            </Node>
            {/** Node added for schema */}
            {pageType === 'Homepage' && (
                <Node className='' vocab='https://schema.org/' typeof='Corporation'>
                    <meta property='name' content='Dobbies' />
                    <meta property='url' content={pageUrl} />
                    <meta property='logo' content={`${pageUrl}static/shop/ui-departmentstore/images/absinthe/logo-large.svg`} />
                    <Node tag='span' className='' property='contactPoint' typeof='ContactPoint'>
                        <meta property='telephone' content='+44 131 561 6406' />
                        <meta property='contactType' content='customer service' />
                        <meta property='contactOption' content='HearingImpairedSupported' />
                        <meta property='areaServed' content='GB' />
                        <meta property='availableLanguage' content='en' />
                    </Node>
                    <meta
                        property='sameAs'
                        content={'['
                            .concat('\nhttps://www.facebook.com/DobbiesGC/')
                            .concat(',')
                            .concat('\nhttps://twitter.com/dobbies')
                            .concat(',')
                            .concat('\nhttps://www.instagram.com/dobbiesgardencentres/')
                            .concat(',')
                            .concat('\nhttps://www.youtube.com/user/DobbiesGardenCentres')
                            .concat(',')
                            .concat('\nhttps://en.wikipedia.org/wiki/Dobbies_Garden_Centres\n')
                            .concat(']')}
                    />
                </Node>
            )}
            {/** Node added for schema */}
            {pageType === 'Homepage' && (
                <Node className='' vocab='https://schema.org/' typeof='WebSite'>
                    <meta property='name' content='Dobbies' />
                    <meta property='url' content={pageUrl} />
                    <Node tag='span' className='' property='potentialAction' typeof='SearchAction'>
                        <meta property='target' content={`${pageUrl}search?q=${searchTermString}`} />
                        <meta property='query-input' content={`required name=${searchTermString}`} />
                    </Node>
                </Node>
            )}
            {/** Modal Added For showing signup error */}
            <Modal
                autoFocus={true}
                fade={false}
                isOpen={isSignupErrorModalOpen}
                applicationNode={'renderPage'}
                horizontalPosition={'center'}
                verticalPosition={'center'}
                zIndex={5003}
                toggle={onSignupErrorModalClose}
                modalClassName='dobbies-club-renewal-popup dobbies-homepage-popup'
                className='dobbies-club-renewal-popup__dialog'
            >
                <ModalHeader>
                    <>{props.config.signupErrorModalHeading}</>
                    {<button type='button' className='msc-modal__close-button' aria-label='Close' onClick={onSignupErrorModalClose} />}
                </ModalHeader>
                <ModalBody className='dobbies-club-renewal-popup__body'>{signupErrorMsg}</ModalBody>
                <ModalFooter>
                    <Button className='homepopup-btn' onClick={redirectToSignin}>
                        Ok
                    </Button>
                </ModalFooter>
            </Modal>
        </Module>
    );
};

const getStoreLinkUrl = (storeLinkUrl: IStoreLinkUrlData): Msdyn365.ILinksData => {
    return {
        ariaLabel: storeLinkUrl && storeLinkUrl.ariaLabel,
        className: 'ms-header__icon-store',
        linkText: storeLinkUrl && storeLinkUrl.linkText,
        linkUrl: storeLinkUrl && storeLinkUrl.linkUrl.destinationUrl,
        openInNewTab: storeLinkUrl && storeLinkUrl.openInNewTab,
        role: 'button'
    };
};
// Function will give the user's tierId (loyalty membership could be club or club plus)
const getTierId = (props: IHeaderViewProps & IHeaderProps<IHeaderData>) => {
    const { loyaltyCard } = props.data;
    // If card number doesn't exist immediately return;
    if (!loyaltyCard.result?.CardNumber) {
        return;
    }
    const loyaltyGroup = loyaltyCard.result?.LoyaltyGroups && loyaltyCard.result?.LoyaltyGroups[0];
    // highestLoyaltCardTier
    const highestActiveLoyaltyCardTier = loyaltyGroup && loyaltyGroup.HighestActiveLoyaltyCardTier;
    const loyaltyCardTiers = loyaltyGroup && loyaltyGroup.LoyaltyCardTiers;
    // Get TierId based on the active cardTier
    if (loyaltyCardTiers && loyaltyCardTiers?.length > 0) {
        for (const [_, loyaltyCardTier] of loyaltyCardTiers.entries()) {
            if (loyaltyCardTier.RecordId === highestActiveLoyaltyCardTier) {
                return loyaltyCardTier.TierId;
            }
        }
    }
    return;
};
const getTotalPoints = (props: IHeaderViewProps & IHeaderProps<IHeaderData>): number => {
    const rewardPoints = props.data.loyaltyCard.result && props.data.loyaltyCard.result.RewardPoints;
    let total = 0;
    rewardPoints!.forEach(loyaltyPoints => {
        total += loyaltyPoints.ActivePoints ?? 0;
    });

    return total;
};
/* VSI Customization - START - 22/10/20 */
/* Updated function parameters to handle state and user menu pop-up*/
function _renderDesktopAccountBlock(
    props: IHeaderViewProps & IHeaderProps<IHeaderData>,
    popOver: boolean,
    togglePopOver: React.Dispatch<React.SetStateAction<boolean>>,
    popOverRef: React.RefObject<HTMLButtonElement>
): JSX.Element | null {
    /* VSI Customization - START - 22/10/20 */
    /* Following function toggle popover */
    function toggleState(): void {
        togglePopOver(!popOver);
    }
    const {
        AccountInfoDropdownParentContainer,
        signInLink,
        signOutLink,
        config: { myAccountLinksBeforeLogin },
        data: {
            accountInformation: { result }
        },
        /* Added following 2 configs for displaying text to greet customer */
        resources: { greetingText }
    } = props;
    /* Following lines checks if user is loggedin or not */
    const isLoggedIn = result && result.Name ? true : false;
    const loyaltyCardData = props.data.loyaltyCard.result;
    const rewardPoints = props.data.loyaltyCard.result && loyaltyCardData?.RewardPoints;
    const totalPoints = rewardPoints && getTotalPoints(props);
    const tierName = getTierId(props);
    const [coffeePerksBalance, setCoffeePerksBalance] = useState<number | undefined>(undefined);
    const [coffeeMembershipError, setCoffeeMembershipError] = React.useState(false);
    if (!coffeePerksBalance) {
        // First call to get customer rewards if any
        const customerCardNo = props.data.loyaltyCard.result?.CardNumber;
        if (customerCardNo && props.config.programID) {
            _getLoyaltyInfo(
                props.context.actionContext,
                customerCardNo,
                props.config.programID,
                props.config.rewardID!,
                props.config.availableRewardID!,
                props.config.extraAvailableRewardID!,
                setCoffeePerksBalance,
                setCoffeeMembershipError
            ).catch((error: Error) => error);
        }
    }
    console.log(coffeeMembershipError);
    const userOptionsDropDownContainer = _getPopoverContainer(props, popOver, togglePopOver, popOverRef);
    /* User Avator will display user menu options when mouse moves on it */
    const userAvatar = _getUserAvatar(props, popOver, togglePopOver, popOverRef);
    /* Updated return data to display avatar and popup options for both loggedin and guest customer.
    Option for both type of customer have some common and some different items. */
    if (AccountInfoDropdownParentContainer) {
        if (userAvatar) {
            return (
                <Node {...AccountInfoDropdownParentContainer} onClick={toggleState} onMouseEnter={toggleState} onMouseLeave={toggleState}>
                    {userAvatar}
                    <Node {...userOptionsDropDownContainer} className='ms-header__user-options'>
                        <div className='slide-down-animation'>
                            {signInLink}
                            {isLoggedIn && result && result.Name && (
                                <Node tag='div' className={'ms-header__greeting-text'}>
                                    {greetingText}
                                    <span>{result.FirstName}</span>
                                </Node>
                            )}
                            {_getAccountLinks(props)}
                            {!isLoggedIn && myAccountLinksBeforeLogin && myAccountLinksBeforeLogin.map(_getAccountLinksForGuest)}
                            {signOutLink}
                            {isLoggedIn && (tierName === 'Club Plus' || tierName === 'Club Plus Free Trial') && (
                                <Node tag='div' className={'ms-account-loyalty__total-point'}>
                                    <div className='loyalty-coffee-dropdown'>
                                        <div className='clubPlus-points-drinks'>
                                            <span>{coffeePerksBalance}</span> hot drinks left this month
                                        </div>
                                    </div>
                                    <div className='loyalty-points-dropdown'>
                                        <div className='clubPlus-points-drinks'>
                                            {' '}
                                            You have <span>{totalPoints && totalPoints.toFixed(0)}</span> points{' '}
                                        </div>
                                    </div>
                                </Node>
                            )}
                            {isLoggedIn && tierName === 'Club' && (
                                <div className='ms-account-loyalty__total-point'>
                                    <div className='loyalty-points-Club-dropdown'>
                                        <div className='club-points-drinks'>
                                            You have <span>{totalPoints && totalPoints.toFixed(0)}</span> points{' '}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </Node>
                </Node>
            );
        }
    }
    /* VSI Customization - END - 23/10/20 */
    props.context.telemetry.error('Header content is empty, module wont render.');
    return null;
}
// this function will return myAccountLinks (coming from CMS)
const _getAccountLinks = (props: IHeaderViewProps & IHeaderProps<IHeaderData>): React.ReactNode[] | undefined => {
    const { myAccountLinks, clubPlusTierText, dobbiesClubLinkText, clubPlusFreeTrialText } = props.config;
    // User's tier ID (loyalty membership could be club or club plus)
    const tierId = getTierId(props);
    const telemetryContent = getTelemetryObject(props.context.request.telemetryPageName!, props.friendlyName, props.telemetry);
    const payLoad = getPayloadObject('click', telemetryContent, '');
    let isLoyaltyinProgress = props.context.request.cookies.get(props.context.request.user.customerAccountNumber!);

    if (isLoyaltyinProgress.status === 'NOT_FOUND') {
        isLoyaltyinProgress = props.context.request.cookies.get('club_plus_purchased');
    }
    if (!props.data.accountInformation.result || !myAccountLinks || !myAccountLinks.length) {
        return undefined;
    }
    return myAccountLinks.map((cta: IMyAccountLinksData, index: number) => {
        // If user's tier is club plus override the url to "loyalty"
        if (
            cta.linkText?.toLowerCase() === dobbiesClubLinkText?.toLowerCase() &&
            (tierId?.toLowerCase() === clubPlusTierText?.toLowerCase() ||
                isLoyaltyinProgress.value === 'true' ||
                tierId?.toLowerCase() === clubPlusFreeTrialText?.toLowerCase())
        ) {
            cta.linkUrl.destinationUrl = Msdyn365.getUrlSync('loyalty', props.context.actionContext) || '';
        }
        payLoad.contentAction.etext = cta.linkText;
        const attributes = getTelemetryAttributes(telemetryContent, payLoad);
        const editableLink: Msdyn365.ILinksData = {
            ariaLabel: cta.ariaLabel,
            className: 'ms-signin-info__account-link-button',
            linkText: cta.linkText,
            linkUrl: cta.linkUrl.destinationUrl,
            openInNewTab: cta.openInNewTab,
            role: 'button',
            additionalProperties: attributes
        };

        return (
            <Msdyn365.Link
                key={index}
                link={editableLink}
                editProps={{ onTextChange: handleLinkTextChange(index, props), requestContext: props.context.request }}
            />
        );
    });
};

/* Handle link text change*/
const handleLinkTextChange = (linkIndex: number, props: IHeaderViewProps & IHeaderProps<IHeaderData>) => (
    event: Msdyn365.ContentEditableEvent
) => {
    if (props.config.myAccountLinks && props.config.myAccountLinks[linkIndex]) {
        props.config.myAccountLinks[linkIndex].linkText = event.target.value;
    }
};

// This function converts linkData to React.ReactNode
const _getAccountLinksForGuest = (link: IMyAccountLinksBeforeLoginData): React.ReactNode => {
    const editableLink: Msdyn365.ILinksData = {
        ariaLabel: link.ariaLabel,
        className: 'ms-guest-user__account-link-button',
        linkText: link.linkText,
        linkUrl: link.linkUrl.destinationUrl,
        openInNewTab: link.openInNewTab,
        role: 'button'
    };

    return <Msdyn365.Link link={editableLink} />;
};

/* Following function displays pop-up menu when mouse moves on avatar.
This function is also available in header.tsx file but re-wrote here just to remove the condition of loggedin user from it.
So that pop-up will be displayed for both loggedin and guest user. */
const _getPopoverContainer = (
    props: IHeaderViewProps & IHeaderProps<IHeaderData>,
    popOver: boolean,
    togglePopOver: React.Dispatch<React.SetStateAction<boolean>>,
    popOverRef: React.RefObject<HTMLButtonElement>
) => {
    return {
        tag: Popover,
        id: 'userOptionsPopover',
        className: 'ms-header__user-options-content',
        placement: 'bottom',
        isOpen: popOver,
        target: popOverRef,
        toggle: togglePopOver
    };
};
/* Following function return avatar which will display pop-up menu on mousehover */
const _getUserAvatar = (
    props: IHeaderViewProps & IHeaderProps<IHeaderData>,
    popOver: boolean,
    togglePopOver: React.Dispatch<React.SetStateAction<boolean>>,
    popOverRef: React.RefObject<HTMLButtonElement>
): React.ReactNode | undefined => {
    function toggleState(): void {
        togglePopOver(!popOver);
    }
    const {
        resources: { userOptionsMenuLabel }
    } = props;
    return (
        <Button
            innerRef={popOverRef}
            className='ms-header__user-options-button'
            aria-describedby='userOptionsPopover'
            onClick={toggleState}
            color='link'
            aria-expanded={popOver}
        >
            <span className='ms-profile-button-text'>{userOptionsMenuLabel}</span>
        </Button>
    );
};

/* Following function returns Signup button for guest user. */
const _getSignUpButton = (props: IHeaderViewProps & IHeaderProps<{}>, isMobile: Boolean): React.ReactNode | undefined => {
    const {
        context: {
            request: {
                user: { signUpUrl }
            }
        },
        resources: { signUpLinkNameforDesktop, signUpLinkNameforMobile }
    } = props;
    const editableLink: Msdyn365.ILinksData = {
        className: isMobile ? 'ms-header__sign-up-button msc-btn' : 'ms-header__sign-up-link msc-btn',
        linkText: isMobile ? signUpLinkNameforMobile : signUpLinkNameforDesktop,
        linkUrl: signUpUrl,
        role: 'button'
    };
    return <Msdyn365.Link link={editableLink} />;
};
/* VSI Customization - END - 23/10/20 */

function _renderReactFragment(items: React.ReactNode[]): JSX.Element | null {
    return (
        <React.Fragment>
            {items && items.length
                ? items.map((slot: React.ReactNode, index: number) => {
                      return <React.Fragment key={index}>{slot}</React.Fragment>;
                  })
                : null}
        </React.Fragment>
    );
}

export default headerView;
